import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { PlaybookSidebar } from './PlaybookSidebar';
import { ClauseDetail } from './ClauseDetail';
import { ConfirmationModal } from './ConfirmationModal';
import { ClauseTypeModal, ClauseTypeFormData, ClauseTypeResult } from './ClauseTypeModal';
import * as api from '../services/apiClient';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { ClauseListItem, ClauseDetailData } from '../shared/playbook-types';
import { logger } from '../utils/logger';
import '../styles/PlaybookCommon.css';

export const MesClausesPage: React.FC = () => {
  const [clauses, setClauses] = useState<ClauseListItem[]>([]);
  const [selectedClauseId, setSelectedClauseId] = useState<number | null>(null);
  const [selectedClauseDetail, setSelectedClauseDetail] = useState<ClauseDetailData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add/Edit modal state
  const [clauseModalOpen, setClauseModalOpen] = useState(false);
  const [clauseModalMode, setClauseModalMode] = useState<'add' | 'edit'>('add');
  const [editClauseData, setEditClauseData] = useState<ClauseTypeFormData | null>(null);
  const [editClauseId, setEditClauseId] = useState<number | null>(null);
  const [editClauseVersions, setEditClauseVersions] = useState<ClauseDetailData['versions'] | undefined>(undefined);
  const [isLoadingForEdit, setIsLoadingForEdit] = useState(false);

  const latestDetailRequestRef = useRef<number>(0);
  const prevAliasRef = useRef<typeof activeAlias | undefined>(undefined);

  const { initialClauseId, setInitialClauseId } = useAppContext();
  const { authStatus } = useAuth();
  const { activeAlias } = useImpersonation();
  const isAdmin = authStatus?.user?.is_admin === true;

  // Handle add request - opens modal in add mode
  const handleAddRequest = useCallback(() => {
    setClauseModalMode('add');
    setEditClauseData(null);
    setEditClauseId(null);
    setEditClauseVersions(undefined);
    setClauseModalOpen(true);
  }, []);

  // Handle edit request - opens modal in edit mode with current data
  const handleEditRequest = useCallback(async (id: number, name: string) => {
    setClauseModalMode('edit');
    setEditClauseId(id);

    // Check if we already have the detail for this specific clause
    if (selectedClauseId === id && selectedClauseDetail) {
      // Use already loaded data
      setEditClauseData({
        name: selectedClauseDetail.name,
        acceptable_terms: selectedClauseDetail.acceptable_terms || '',
        unacceptable_terms: selectedClauseDetail.unacceptable_terms || '',
      });
      setEditClauseVersions(selectedClauseDetail.versions);
      setClauseModalOpen(true);
    } else {
      // Need to fetch the detail first
      setIsLoadingForEdit(true);
      try {
        const response = await api.getClauseDetail(id);
        if (response.success && response.data) {
          setEditClauseData({
            name: response.data.name,
            acceptable_terms: response.data.acceptable_terms || '',
            unacceptable_terms: response.data.unacceptable_terms || '',
          });
          setEditClauseVersions(response.data.versions);
          setClauseModalOpen(true);
        } else {
          logger.error('Erreur lors du chargement des détails pour édition:', response.error);
          alert(response.error || 'Erreur lors du chargement des détails');
        }
      } catch (err) {
        logger.error('Erreur lors du chargement des détails pour édition:', err);
        alert('Erreur lors du chargement des détails');
      } finally {
        setIsLoadingForEdit(false);
      }
    }
  }, [selectedClauseId, selectedClauseDetail]);

  // Handle clause modal success
  const handleClauseModalSuccess = useCallback((result: ClauseTypeResult) => {
    // Update the list immediately
    setClauses(prev => {
      const existingIndex = prev.findIndex(c => c.id === result.id);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = { id: result.id, name: result.name };
        return updated;
      } else {
        // Add new at the end
        return [...prev, { id: result.id, name: result.name }];
      }
    });

    // If we edited the currently selected clause, update its detail too
    if (selectedClauseId === result.id) {
      setSelectedClauseDetail(prev => prev ? {
        ...prev,
        name: result.name,
        acceptable_terms: result.acceptable_terms,
        unacceptable_terms: result.unacceptable_terms,
      } : null);
    }

    // If it's a new clause, select it
    if (clauseModalMode === 'add') {
      setSelectedClauseId(result.id);
    }
  }, [selectedClauseId, clauseModalMode]);

  // Handle delete request (opens modal)
  const handleDeleteRequest = useCallback((id: number, name: string) => {
    setItemToDelete({ id, name });
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await api.deleteClause(itemToDelete.id);

      if (response.success) {
        // Remove from list
        setClauses(prev => prev.filter(c => c.id !== itemToDelete.id));

        // Deselect if it was the selected item
        if (selectedClauseId === itemToDelete.id) {
          setSelectedClauseId(null);
          setSelectedClauseDetail(null);
        }

        logger.info(`Clause supprimée: ${itemToDelete.id} (${itemToDelete.name})`);
      } else {
        logger.error('Erreur lors de la suppression:', response.error);
        alert(response.error || 'Erreur lors de la suppression de la clause');
      }
    } catch (err) {
      logger.error('Erreur lors de la suppression:', err);
      alert('Erreur lors de la suppression de la clause');
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, selectedClauseId, isDeleting]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  }, []);

  useEffect(() => {
    if (initialClauseId) {
      setSelectedClauseId(initialClauseId);
      setInitialClauseId(null);
    }
  }, [initialClauseId, setInitialClauseId]);

  // Reset selection when impersonation changes (skip initial mount)
  useEffect(() => {
    // Only reset if this is an actual alias change, not initial mount
    if (prevAliasRef.current !== undefined && prevAliasRef.current !== activeAlias) {
      setSelectedClauseId(null);
      setSelectedClauseDetail(null);
    }
    prevAliasRef.current = activeAlias;
  }, [activeAlias]);

  const fetchClauses = useCallback(async () => {
    console.log('[MesClausesPage] fetchClauses called, activeAlias:', activeAlias);
    setIsLoadingList(true);
    setError(null);
    try {
      const response = await api.getClauses();
      console.log('[MesClausesPage] getClauses response:', response.success, 'count:', response.data?.length);
      if (response.success && response.data) {
        setClauses(response.data);
      } else {
        setError(response.error || 'Échec du chargement des clauses');
        logger.error('Error fetching clauses list:', response.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      logger.error('Error fetching clauses list:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [activeAlias]);

  useEffect(() => {
    fetchClauses();
  }, [fetchClauses]);

  const fetchClauseDetail = useCallback(async (clauseId: number) => {
    const requestId = ++latestDetailRequestRef.current;
    setIsLoadingDetail(true);
    setDetailError(null);
    try {
      const response = await api.getClauseDetail(clauseId);

      if (requestId !== latestDetailRequestRef.current) {
        return;
      }

      if (response.success && response.data) {
        setSelectedClauseDetail(response.data as ClauseDetailData);
      } else {
        logger.error(`Error fetching clause detail ${clauseId}:`, response.error);
        setSelectedClauseDetail(null);
        setDetailError(response.error || 'Échec du chargement des détails');
      }
    } catch (err) {
      if (requestId !== latestDetailRequestRef.current) {
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      logger.error(`Error fetching clause detail ${clauseId}:`, err);
      setSelectedClauseDetail(null);
      setDetailError(errorMessage);
    } finally {
      if (requestId === latestDetailRequestRef.current) {
        setIsLoadingDetail(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedClauseId) {
      setSelectedClauseDetail(null);
      setDetailError(null);
      return;
    }

    fetchClauseDetail(selectedClauseId);
  }, [selectedClauseId, fetchClauseDetail]);

  // Refresh handler for version updates
  const handleRefreshDetail = useCallback(() => {
    if (selectedClauseId) {
      fetchClauseDetail(selectedClauseId);
    }
  }, [selectedClauseId, fetchClauseDetail]);

  return (
    <div className="playbook-page">
      <div className="playbook-container">
        {error ? (
          <div className="playbook-sidebar">
            <div className="playbook-sidebar-header">
              <h2>Mes clauses</h2>
            </div>
            <div className="playbook-error-state">
              <p>{error}</p>
              <button onClick={fetchClauses} className="retry-button">
                Réessayer
              </button>
            </div>
          </div>
        ) : (
          <PlaybookSidebar
            title="Mes clauses"
            items={clauses}
            selectedId={selectedClauseId}
            onSelectItem={setSelectedClauseId}
            isLoading={isLoadingList}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Rechercher une clause"
            emptyMessage="Aucune clause trouvée"
            onAdd={isAdmin ? handleAddRequest : undefined}
            onEditItem={isAdmin ? handleEditRequest : undefined}
            onDeleteItem={isAdmin ? handleDeleteRequest : undefined}
          />
        )}

        <div className="playbook-main-content">
          <ClauseDetail
            clause={selectedClauseDetail}
            isLoading={isLoadingDetail}
            error={detailError}
            onRetry={() => {
              if (selectedClauseId) {
                setDetailError(null);
                const id = selectedClauseId;
                setSelectedClauseId(null);
                setTimeout(() => setSelectedClauseId(id), 0);
              }
            }}
            onEdit={isAdmin ? handleEditRequest : undefined}
            onDelete={isAdmin ? handleDeleteRequest : undefined}
            isAdmin={isAdmin}
            onRefresh={handleRefreshDetail}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Supprimer cette clause ?"
        message="Cette action est irréversible. La clause sera définitivement supprimée."
        isLoading={isDeleting}
      />

      <ClauseTypeModal
        isOpen={clauseModalOpen}
        onClose={() => setClauseModalOpen(false)}
        mode={clauseModalMode}
        initialData={editClauseData || undefined}
        clauseId={editClauseId || undefined}
        initialVersions={clauseModalMode === 'edit' ? editClauseVersions : undefined}
        onSubmitSuccess={handleClauseModalSuccess}
        onRefreshNeeded={handleRefreshDetail}
      />
    </div>
  );
};
